// The invite code from a referral link — `/auth?ref=CODE`, the shape the people
// service mints into `referral_link`. Stored in sessionStorage (not router state)
// because it must survive the /auth → /sign-up navigation, a reload of either screen,
// and the OAuth full-page redirect. Same reasoning as auth-redirect.ts.
const KEY = 'auth:referral-code';

export const REFERRAL_PARAM = 'ref';

// `dto.CreateUser.referral_code` (auth service) is capped at 32 characters. A longer
// value is not a code this backend can accept, so it is dropped rather than truncated
// into a different — possibly valid — code.
const MAX_LENGTH = 32;

const isValidCode = (code: string): boolean => !!code && code.length <= MAX_LENGTH;

/**
 * Reads the referral code out of a location search string and remembers it.
 *
 * Returns the code in play: the one just captured, or one captured earlier in this tab
 * — landing on /auth a second time without the param must not forget the invite.
 */
export const captureReferralCode = (search: string): string | null => {
  const code = new URLSearchParams(search).get(REFERRAL_PARAM)?.trim() ?? '';
  if (isValidCode(code)) sessionStorage.setItem(KEY, code);
  return getReferralCode();
};

export const getReferralCode = (): string | null => {
  const code = sessionStorage.getItem(KEY);
  return code && isValidCode(code) ? code : null;
};

/**
 * Forget the invite. Called once registration is confirmed, so the code cannot be
 * attached to a second account created later in the same tab.
 */
export const clearReferralCode = (): void => sessionStorage.removeItem(KEY);
